import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, Skeleton } from '../../components/shadcn/index.js';
import { api } from '../../lib/api.js';

/**
 * Settings → Printing: the shop's document-sheet defaults, editable in the open.
 *
 * The print page keeps every sheet preference as an `erp-doc-*` localStorage
 * entry and seeds a fresh browser from the tenant setting `doc_print`. Until
 * this page existed the ONLY way to write that setting was the print page's
 * "save these settings for the shop" button — a snapshot of one browser's
 * choices, with no way to see or change a single value. This page edits the
 * same setting field by field.
 *
 * Two rules it must keep:
 *  - PRESERVE what it does not own. `doc_print` also carries the per-document
 *    column layouts (`erp-doc-cols:*`, widths, summary rows) that only the
 *    print page can meaningfully edit — a save here must not drop them.
 *  - A browser that chose for itself KEEPS its choice (the seed fills only
 *    unset keys). So a save here also writes the value into THIS browser's
 *    localStorage — the owner changing a default expects to see it on their
 *    own next print, not only on machines that never printed before.
 */

const SETTING_KEY = 'doc_print';

/** The print page's own storage keys — same names, so the two cannot drift. */
const K = {
  lang: 'erp-doc-lang',
  dense: 'erp-doc-dense',
  bold: 'erp-doc-bold',
  notes: 'erp-doc-notes',
  barcodes: 'erp-doc-line-barcodes',
  headtint: 'erp-doc-headtint',
  tint: 'erp-doc-tint',
  font: 'erp-doc-font',
  type: 'erp-doc-type',
  a5head: 'erp-doc-a5head',
  a5foot: 'erp-doc-a5foot',
  top: 'erp-doc-plain-top',
  bottom: 'erp-doc-plain-bottom',
  side: 'erp-doc-plain-side',
} as const;

/** What the print page does when a key is absent — shown here as the initial
 *  state so the page reads as the sheet actually prints. */
const DEFAULTS: Record<string, string> = {
  [K.lang]: '',
  [K.dense]: '1',
  [K.bold]: '0',
  [K.notes]: '1',
  [K.barcodes]: '1',
  [K.headtint]: '1',
  [K.tint]: '--tint-1',
  [K.font]: '',
  [K.type]: '1',
  [K.a5head]: '0',
  [K.a5foot]: '0',
  [K.top]: '40',
  [K.bottom]: '30',
  [K.side]: '10',
};

/** The print page's five head washes, by the custom-property NAME it stores.
 *  Values live in the scoped stylesheet below (same hexes the print page
 *  declares), so the swatches here show exactly what the sheet will print. */
const TINTS = ['--tint-1', '--tint-2', '--tint-3', '--tint-4', '--tint-5'] as const;
const TINT_CSS = `
.print-settings { --tint-1: #E1ECFF; --tint-2: #EDEFF2; --tint-3: #E4F2E8; --tint-4: #FBF0DA; --tint-5: #F6E3E6; }
`;

const FONTS = ['', 'f-sans', 'f-system', 'f-serif'] as const;
const TYPE_STEPS = ['0.85', '0.925', '1', '1.075', '1.15'] as const;
const LANGS = ['', 'ar', 'en', 'both'] as const;

function Toggle({ on, label, disabled, onChange }: {
  on: boolean; label: string; disabled: boolean; onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition disabled:opacity-50 ${on ? 'bg-brand' : 'bg-border'}`}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${on ? 'translate-x-5' : 'translate-x-1'}`} />
    </button>
  );
}

export function PrintingSettingsPage(): JSX.Element {
  const { t } = useTranslation('settings');
  const [loaded, setLoaded] = useState(false);
  // The WHOLE stored map, unknown keys included — the save writes it back entire.
  const [stored, setStored] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api()
      .get<{ items: Array<{ key: string; value: unknown }> }>('/settings')
      .then((r) => {
        const v = r.data.items.find((x) => x.key === SETTING_KEY)?.value;
        const map: Record<string, string> = {};
        if (v && typeof v === 'object') {
          for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
            if (typeof val === 'string') map[k] = val;
          }
        }
        setStored(map);
      })
      .catch(() => setError(t('printing.errors.loadFailed', { defaultValue: 'Could not load the printing settings.' })))
      .finally(() => setLoaded(true));
  }, [t]);

  /** The effective value of one owned key: stored, else the sheet's default. */
  const val = (k: string): string => stored[k] ?? DEFAULTS[k] ?? '';

  const save = async (k: string, v: string): Promise<void> => {
    setSaving(true);
    setError(null);
    setSavedNote(null);
    const prev = stored;
    const next = { ...stored, [k]: v };
    setStored(next); // optimistic — reverted on failure
    try {
      await api().put(`/settings/${SETTING_KEY}`, { value: next });
      // This browser follows immediately — the shop seed only fills machines
      // that never chose, and the person on THIS page expects to see the change.
      try { window.localStorage.setItem(k, v); } catch { /* private mode */ }
      setSavedNote(t('printing.saved', { defaultValue: 'Saved — new browsers start from this; every machine can still adjust its own from the print page.' }));
    } catch {
      setStored(prev);
      setError(t('printing.errors.saveFailed', { defaultValue: 'Could not save. Try again.' }));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-40 w-full" />
      </Card>
    );
  }

  const row = (label: string, help: string, control: JSX.Element): JSX.Element => (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        {help && <p className="mt-1 text-sm text-fg-muted">{help}</p>}
      </div>
      {control}
    </div>
  );

  const toggleRow = (k: string, label: string, help: string): JSX.Element =>
    row(label, help, (
      <Toggle
        on={val(k) === '1'}
        label={label}
        disabled={saving}
        onChange={(on) => void save(k, on ? '1' : '0')}
      />
    ));

  const selectCls = 'h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg';

  return (
    <Card className="print-settings p-6">
      <style>{TINT_CSS}</style>
      <h2 className="mb-2 text-lg font-semibold">{t('printing.title', { defaultValue: 'Printing' })}</h2>
      <p className="mb-6 text-sm text-fg-muted">
        {t('printing.description', {
          defaultValue: 'The shop-wide defaults for every printed document (invoices, orders, bills). A machine that changed a setting from the print page itself keeps its own choice.',
        })}
      </p>

      <div className="flex flex-col gap-3">
        {row(
          t('printing.lang.label', { defaultValue: 'Document language' }),
          t('printing.lang.help', { defaultValue: 'The language of the PAPER — the dashboard keeps its own. "Both" prints every label in Arabic and English.' }),
          <select
            className={selectCls}
            value={val(K.lang)}
            disabled={saving}
            aria-label={t('printing.lang.label', { defaultValue: 'Document language' })}
            onChange={(e) => void save(K.lang, e.target.value)}
          >
            {LANGS.map((l) => (
              <option key={l || 'app'} value={l}>
                {t(`printing.lang.${l || 'app'}`, {
                  defaultValue: { '': 'Follow the app', ar: 'العربية', en: 'English', both: 'AR+EN' }[l],
                })}
              </option>
            ))}
          </select>,
        )}

        {row(
          t('printing.dense.label', { defaultValue: 'Compact — more lines per page' }),
          t('printing.dense.help', { defaultValue: 'How tightly the A4 packs its rows: Normal is the airy original; Compact shrinks the letterhead, gaps, photos and margins; Extra also steps the line type down one size.' }),
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={0}
              max={2}
              step={1}
              className="w-32 accent-brand"
              value={Math.min(2, Math.max(0, Number(val(K.dense)) || 0))}
              disabled={saving}
              aria-label={t('printing.dense.label', { defaultValue: 'Compact — more lines per page' })}
              onChange={(e) => void save(K.dense, String(Math.min(2, Math.max(0, Number(e.target.value) || 0))))}
            />
            <span className="min-w-16 text-sm font-semibold text-fg-muted">
              {t(`printing.dense.level${Math.min(2, Math.max(0, Number(val(K.dense)) || 0))}`, {
                defaultValue: ['Normal', 'Compact', 'Extra'][Math.min(2, Math.max(0, Number(val(K.dense)) || 0))],
              })}
            </span>
          </div>,
        )}
        {toggleRow(
          K.bold,
          t('printing.bold.label', { defaultValue: 'Bold text' }),
          t('printing.bold.help', { defaultValue: 'Everything prints one weight heavier — for a printer whose toner is fading.' }),
        )}
        {toggleRow(
          K.notes,
          t('printing.notes.label', { defaultValue: "The document's notes" }),
          t('printing.notes.help', { defaultValue: 'Print the notes saved on the document. Till sales write bookkeeping notes a customer need not read.' }),
        )}
        {toggleRow(
          K.barcodes,
          t('printing.barcodes.label', { defaultValue: 'Item barcode numbers' }),
          t('printing.barcodes.help', { defaultValue: 'Each line shows its product barcode as digits.' }),
        )}

        {row(
          t('printing.font.label', { defaultValue: 'Font' }),
          t('printing.font.help', { defaultValue: '"Original" is the face of the shop\'s old paper.' }),
          <select
            className={selectCls}
            value={val(K.font)}
            disabled={saving}
            aria-label={t('printing.font.label', { defaultValue: 'Font' })}
            onChange={(e) => void save(K.font, e.target.value)}
          >
            {FONTS.map((f) => (
              <option key={f || 'default'} value={f}>
                {t(`printing.font.${f || 'default'}`, {
                  defaultValue: { '': 'Original', 'f-sans': 'Sans', 'f-system': 'System', 'f-serif': 'Serif' }[f],
                })}
              </option>
            ))}
          </select>,
        )}

        {row(
          t('printing.size.label', { defaultValue: 'Text size' }),
          t('printing.size.help', { defaultValue: 'Smaller type fits more lines; larger reads easier.' }),
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={0}
              max={TYPE_STEPS.length - 1}
              step={1}
              className="w-32 accent-brand"
              value={TYPE_STEPS.includes(val(K.type) as (typeof TYPE_STEPS)[number]) ? TYPE_STEPS.indexOf(val(K.type) as (typeof TYPE_STEPS)[number]) : 2}
              disabled={saving}
              aria-label={t('printing.size.label', { defaultValue: 'Text size' })}
              onChange={(e) => {
                const v = TYPE_STEPS[Math.min(TYPE_STEPS.length - 1, Math.max(0, Number(e.target.value) || 0))]!;
                void save(K.type, v);
              }}
            />
            <span className="min-w-12 text-sm font-semibold text-fg-muted">{`${Math.round(Number(val(K.type)) * 100)}%`}</span>
          </div>,
        )}

        {row(
          t('printing.tint.label', { defaultValue: 'Table head colour' }),
          t('printing.tint.help', { defaultValue: 'The wash behind the items table heading.' }),
          <div className="flex gap-2" role="group" aria-label={t('printing.tint.label', { defaultValue: 'Table head colour' })}>
            {TINTS.map((name) => (
              <button
                key={name}
                type="button"
                disabled={saving}
                onClick={() => void save(K.tint, name)}
                className={`h-7 w-7 rounded-md border border-border ${val(K.tint) === name ? 'outline outline-2 outline-brand outline-offset-1' : ''}`}
                style={{ background: `var(${name})` }}
                aria-label={name}
                aria-pressed={val(K.tint) === name}
              />
            ))}
          </div>,
        )}
      </div>

      {/* A5 "plain" — the mode that prints data onto the shop's own pre-printed
          stationery. Its blank margins are a property of the paper stack. */}
      <h3 className="mb-2 mt-8 text-sm font-bold uppercase tracking-wide text-fg-muted">
        {t('printing.a5.title', { defaultValue: 'A5 — pre-printed paper' })}
      </h3>
      <div className="flex flex-col gap-3">
        {toggleRow(
          K.a5head,
          t('printing.a5.head', { defaultValue: 'Print the letterhead' }),
          t('printing.a5.headHelp', { defaultValue: 'Off when the paper already carries it.' }),
        )}
        {toggleRow(
          K.a5foot,
          t('printing.a5.foot', { defaultValue: 'Print the footer' }),
          '',
        )}
        {toggleRow(
          K.headtint,
          t('printing.a5.headtint', { defaultValue: 'Coloured table head' }),
          '',
        )}
        {row(
          t('printing.a5.margins', { defaultValue: 'Blank margins (mm)' }),
          t('printing.a5.marginsHelp', { defaultValue: 'The space left for what is already printed on the paper: top · bottom · side.' }),
          <div className="flex gap-2">
            {([['top', K.top, 150], ['bottom', K.bottom, 90], ['side', K.side, 40]] as const).map(([which, k, max]) => (
              <input
                key={k}
                type="number"
                min={0}
                max={max}
                className="h-9 w-16 rounded-md border border-border bg-bg px-2 text-center text-sm text-fg"
                value={val(k)}
                disabled={saving}
                aria-label={t(`printing.a5.${which}`, { defaultValue: which })}
                onChange={(e) => {
                  const n = Math.min(max, Math.max(0, Math.round(Number(e.target.value) || 0)));
                  void save(k, String(n));
                }}
              />
            ))}
          </div>,
        )}
      </div>

      <p className="mt-6 text-xs text-fg-muted">
        {t('printing.columnsHint', {
          defaultValue: 'Which columns each document type prints — and their widths — are chosen on the print page itself (Sheet options), where the paper is in front of you.',
        })}
      </p>

      {error && <p className="mt-4 text-sm text-danger" role="alert">{error}</p>}
      {savedNote && <p className="mt-4 text-sm text-success" role="status">{savedNote}</p>}
    </Card>
  );
}
